import * as React from "react";
import {
  TextField,
  EditButton,
  TextInput,
  FunctionField,
  TopToolbar,
  CreateButton,
  ExportButton,
  useNotify,
  useRefresh,
} from "react-admin";
import * as Ra from "react-admin";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import { CountryEnum } from "../../../common/enum";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Popover,
  Select,
  Typography,
} from "@mui/material";
import HttpClient from "src/providers/HttpClient";
import ApiResources from "src/providers/ApiResources";
import MultipartUpload from "src/providers/MultipartUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import { Datagrid, IfCanAccess, List } from "src/enterprise/ra-rbac";

const YesNoDialog = (props) => {
  const open = props.open;
  const setOpen = props.setOpen;
  const handleClose = props.handleClose;

  const close = () => {
    setOpen(false);
  };

  const negativeAnswer = () => {
    handleClose(false);
  };

  const positiveAnswer = () => {
    handleClose(true);
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">
        {"Detele All Tax Rates?"}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          Are you sure you want to delete all tax rates in this tax class?
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={negativeAnswer}>No</Button>
        <Button onClick={positiveAnswer} autoFocus>
          Yes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface option {
  id: number;
  label: string;
}

const ListActions = () => {
  const [taxes, setTaxes] = React.useState<option[]>([]);
  const [importValue, setImportValue] = React.useState(taxes[0]?.id);
  const [deleteValue, setDeleteValue] = React.useState(taxes[0]?.id);
  const [file, setFile] = React.useState(null);
  const notify = useNotify();
  const refresh = useRefresh();

  const [anchorElImport, setAnchorElImport] = React.useState(null);
  const [anchorElDelete, setAnchorElDelete] = React.useState(null);

  const [openPrompt, setOpenPrompt] = React.useState(false);

  const handleClickImport = (event) => {
    setAnchorElImport(event.currentTarget);
  };

  const handleCloseImport = () => {
    setAnchorElImport(null);
  };

  const handleClickDelete = (event) => {
    setAnchorElDelete(event.currentTarget);
  };

  const handleCloseDelete = () => {
    setAnchorElDelete(null);
  };

  const openImport = Boolean(anchorElImport);
  const openDelete = Boolean(anchorElDelete);
  const idImport = openImport ? "simple-popover" : undefined;
  const idDelete = openDelete ? "simple-popover" : undefined;

  React.useEffect(() => {
    HttpClient(ApiResources.getTaxes(), {
      method: "GET",
    }).then(({ json }) => {
      let result = json.results as any;
      let data = result.map((tax) => {
        let newObj = { id: tax.id, label: tax.name };
        return newObj;
      });
      setTaxes(data);
      setImportValue(data[0].id);
      setDeleteValue(data[0].id);
    }).catch(error => {
      notify(error.message, { type: 'error' });
    });
  }, []);

  const handleChange = (event) => {
    setImportValue(event.target.value);
  };

  const handleChangeDeleteValue = (event) => {
    setDeleteValue(event.target.value);
  };

  const selectFile = (event) => {
    let f = event.target.files[0];
    setFile(f);
  };

  const Import = () => {
    let data = new FormData();
    data.append("file", file);

    MultipartUpload(ApiResources.importTaxItemsCSV(importValue), data)
      .then((res) => res.json())
      .then((res) => {
        setFile(null);
        handleCloseImport();
        if (res.code >= 400) throw Error;
        notify("Successful Import.", { type: "success" });
        refresh();
      })
      .catch(() => {
        notify("Something Went Wrong!", { type: "error" });
      });
  };
  const Delete = () => {
    HttpClient(ApiResources.deleteAllTaxItems(deleteValue), {
      method: "DELETE",
    }).then(({ json }) => {
      handleCloseDelete();
      notify("Tax Item Deleted.", { type: "success" });
      refresh();
    }).catch(error => {
      notify(error.message, { type: 'error' });
    });
  };

  const handleClosePrompt = (answer) => {
    setOpenPrompt(false);
    if (answer) Delete();
  };

  return (
    <TopToolbar>
      <CreateButton />
      <Ra.Button onClick={handleClickImport} label="Import">
        <FileUploadIcon />
      </Ra.Button>
      <Ra.Button label="Delete All" onClick={handleClickDelete}>
        <DeleteIcon />
      </Ra.Button>
      <ExportButton />
      <YesNoDialog
        open={openPrompt}
        setOpen={setOpenPrompt}
        handleClose={handleClosePrompt}
      />
      <Popover
        id={idImport}
        open={openImport}
        anchorEl={anchorElImport}
        onClose={handleCloseImport}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Box
          sx={{
            p: 3,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <FormControl fullWidth>
            <InputLabel id="demo-simple-select-label">Tax Class</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={importValue}
              label="Tax Class"
              onChange={handleChange}
            >
              {taxes.map((tax, index) => (
                <MenuItem value={tax.id} key={index}>
                  {tax.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ display: "flex", mt: 1 }}>
            <Button variant="outlined" component="label">
              Select File
              <input
                type="file"
                accept="text/csv"
                onChange={selectFile}
                hidden
              />
            </Button>
            <Typography variant="h6" sx={{ ml: 1 }}>
              {file?.name}
            </Typography>
          </Box>
          <div>
            <Link
              href="https://d1qubha584ihnw.cloudfront.net/common/tax_rates.sample.csv"
              variant="body1"
            >
              Sample CSV
            </Link>
          </div>
          <Button
            variant="contained"
            startIcon={<FileUploadIcon />}
            sx={{ mt: 2 }}
            onClick={Import}
            disabled={!file}
          >
            Import
          </Button>
        </Box>
      </Popover>
      <Popover
        id={idDelete}
        open={openDelete}
        anchorEl={anchorElDelete}
        onClose={handleCloseDelete}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Box
          sx={{
            p: 3,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <FormControl fullWidth>
            <InputLabel id="demo-simple-select-label">Tax Class</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={deleteValue}
              label="Tax Class"
              onChange={handleChangeDeleteValue}
            >
              {taxes.map((tax, index) => (
                <MenuItem value={tax.id} key={index}>
                  {tax.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<DeleteIcon />}
            sx={{ mt: 2 }}
            onClick={() => setOpenPrompt(true)}
          >
            Delete
          </Button>
        </Box>
      </Popover>
    </TopToolbar>
  );
};

const listFilters = [<TextInput source="search" label="Search" alwaysOn />];

export const TaxRatesList = (props) => (
  <List
    {...props}
    filters={listFilters}
    queryOptions={{ cacheTime: 0 }}
    actions={<ListActions />}
  >
    <Datagrid>
      <TextField source="id" />
      <TextField source="tax.name" label="Tax Name" />
      <TextField source="rate" />
      <TextField source="state" />
      <FunctionField
        label="Country"
        source="country"
        render={(record) =>
          CountryEnum.find((country) => country.id === record.country)?.name
        }
      />
      <TextField source="zip" />
      <IfCanAccess action="edit">
        <EditButton />
      </IfCanAccess>
    </Datagrid>
  </List>
);
